import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import { useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { FaArrowLeft, FaDownload, FaPrint } from 'react-icons/fa'
import { BsArrowClockwise, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'

import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import { NO_IMAGE_SRC, resolveInstituteLogoSrc, getInstituteLogoPathFromRecord } from '../utils/instituteBranding'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import { useInstitute } from './commonComponents/InstituteContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import AdmitCardPrintView from './AdmitCardPrintView'
import AdmitCardShriHit1 from '../AdmitCardShriHit1'
import {
  normalizeId,
  getFilteredSubjects,
  getSubjectRows,
  buildExamRows,
  mapSubjectSchedulesToAdmitRows,
} from './admitCardHelpers'

/** Matches API check for Shri Hit admit card template */
const isShriHitAdmitCardCode = (code) => {
  const normalized = String(code || '').toUpperCase()
  return normalized === 'SHRIHITADMITCARD1'
}

const hasShriHitAdmitCardTemplate = (templates) => {
  if (!Array.isArray(templates)) return false
  return templates.some(
    (item) =>
      String(item?.template_type || '').toLowerCase() === 'admit_card' &&
      isShriHitAdmitCardCode(item?.template_code),
  )
}

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const fetchImageAsDataUrlForPdf = async (url) => {
  if (!url || url.startsWith('data:')) return url
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('Direct logo fetch failed for PDF:', error)
  }
  try {
    const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const response = await fetch(proxyUrl)
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('Proxy logo fetch failed for PDF:', error)
  }
  return url
}

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })

const waitForImages = (root) => {
  const images = Array.from(root?.querySelectorAll('img') || [])
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth) {
            resolve()
            return
          }
          const done = () => resolve()
          img.addEventListener('load', done, { once: true })
          img.addEventListener('error', done, { once: true })
          window.setTimeout(done, 4000)
        }),
    ),
  )
}

const inlineImagesForPdf = async (root) => {
  const images = Array.from(root?.querySelectorAll('img') || [])
  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src')
      if (!src || src.startsWith('data:')) return
      const dataUrl = await fetchImageAsDataUrlForPdf(src)
      if (dataUrl) img.setAttribute('src', dataUrl)
    }),
  )
}

const waitForAdmitCardNodes = async (expectedCount, timeoutMs = 10000) => {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const nodes = document.querySelectorAll(
      '.exam-sheet-bulk-admit-root .admit-card-root',
    )
    if (nodes.length >= expectedCount && expectedCount > 0) {
      return Array.from(nodes)
    }
    await new Promise((resolve) => window.setTimeout(resolve, 80))
  }
  return Array.from(
    document.querySelectorAll('.exam-sheet-bulk-admit-root .admit-card-root'),
  )
}

const getStudentId = (student) => String(student?.id ?? '')

const getStudentSearchText = (student) => {
  if (!student || typeof student !== 'object') return ''
  const parts = [
    student.name,
    student.student_name,
    student.full_name,
    student.first_name,
    student.last_name,
    student.father_name,
    student.admission_number,
    student.admission_no,
    student.roll_number,
    student.roll_no,
    student.email,
    student.mobile,
    student.phone,
  ]
  Object.values(student).forEach((value) => {
    if (typeof value === 'string' || typeof value === 'number') {
      parts.push(value)
    }
  })
  return parts.filter(Boolean).join(' ').toLowerCase()
}

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const loadProfilesByIds = async (ids, existingProfiles) => {
  const missing = ids.filter((id) => id && !existingProfiles[id])
  if (!missing.length) return {}

  const loaded = {}
  const batchSize = 5
  for (let i = 0; i < missing.length; i += batchSize) {
    const batch = missing.slice(i, i + batchSize)
    const results = await Promise.all(
      batch.map(async (id) => {
        try {
          const res = await ApiEndpoints.viewProfile(id)
          return [id, res?.data?.data || null]
        } catch {
          return [id, null]
        }
      }),
    )
    results.forEach(([id, data]) => {
      if (data) loaded[id] = data
    })
  }
  return loaded
}

export default function ExamSheetBulkAdmitcard() {
  const { token, logout, user } = useAuth()
  const authToken = token || user?.token
  const { isCollapsed } = useSidebar()
  const {
    name: instituteNameFromContext,
    logo: instituteLogoFromContext,
    id: instituteIdFromContext,
  } = useInstitute()
  const navigate = useNavigate()
  const institute_id = instituteIdFromContext || Cookies.get('institute_id')
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const normalizedRoles = (user?.roles || []).map((role) => String(role).toLowerCase())
  const isAdminUser = normalizedUserType === 'admin' || normalizedRoles.includes('admin')

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [fetchedInstituteLogo, setFetchedInstituteLogo] = useState('')
  const [showFilterCard, setShowFilterCard] = useState(true)

  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [students, setStudents] = useState([])
  const [selectedCourse, setSelectedCourse] = useState('')
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedExam, setSelectedExam] = useState('')
  const [selectedStudentIds, setSelectedStudentIds] = useState([])
  const [studentSearch, setStudentSearch] = useState('')
  const [studentSortBy, setStudentSortBy] = useState('')
  const [loadingStudents, setLoadingStudents] = useState(false)

  const [profiles, setProfiles] = useState({})
  const [subjects, setSubjects] = useState([])
  const [exams, setExams] = useState([])
  const [subjectSchedules, setSubjectSchedules] = useState([])
  const [examLocation, setExamLocation] = useState('')
  const [loadingCard, setLoadingCard] = useState(false)
  const [loadingSchedules, setLoadingSchedules] = useState(false)
  const [preparingAction, setPreparingAction] = useState('')
  const [errorCard, setErrorCard] = useState('')
  const [pdfInstituteLogo, setPdfInstituteLogo] = useState('')
  const [exportingAll, setExportingAll] = useState(false)
  const [useShriHitTemplate, setUseShriHitTemplate] = useState(false)
  const printContainerRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    const fetchTemplateCodes = async () => {
      const instituteId = Number(institute_id)
      const token = authToken

      if (!Number.isFinite(instituteId) || instituteId <= 0 || !token) {
        if (!cancelled) {
          setUseShriHitTemplate(false)
        }
        return
      }

      try {
        const response = await axios.get(`${BASE_URL}/templates/codes`, {
          params: { template_type: 'admit_card' },
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Institute-Id': instituteId,
          },
        })

        const templates = Array.isArray(response.data?.data)
          ? response.data.data
          : []

        if (!cancelled) {
          setUseShriHitTemplate(hasShriHitAdmitCardTemplate(templates))
        }
      } catch {
        if (!cancelled) {
          setUseShriHitTemplate(false)
        }
      }
    }

    fetchTemplateCodes()
    return () => {
      cancelled = true
    }
  }, [authToken, institute_id])

  useEffect(() => {
    if (user && !isAdminUser) {
      navigate('/ExamSheet/Admitcard', { replace: true })
    }
  }, [user, isAdminUser, navigate])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    ApiEndpoints.getAllCourses().then((res) => setCourses(res.data.data || []))
  }, [])

  const examHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${authToken}`,
      'institute-Id': Number(institute_id),
    }),
    [authToken, institute_id],
  )

  useEffect(() => {
    if (!selectedCourse || !authToken) {
      setExams([])
      return
    }
    let cancelled = false
    axios
      .get(`${BASE_URL}/get/exam/all`, { headers: examHeaders })
      .then((res) => {
        if (!cancelled) setExams(Array.isArray(res.data?.data) ? res.data.data : [])
      })
      .catch(() => {
        if (!cancelled) setExams([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedCourse, authToken, examHeaders])

  const instituteName =
    instituteNameFromContext || Cookies.get('name') || Cookies.get('institute_name') || ''

  const instituteDisplayLogo = useMemo(() => {
    const toHttps = (url) =>
      typeof url === 'string' ? url.replace(/^http:\/\//i, 'https://') : url

    if (instituteLogoFromContext && instituteLogoFromContext !== NO_IMAGE_SRC) {
      return toHttps(instituteLogoFromContext)
    }
    const cookieLogo = Cookies.get('institute_logo')
    if (cookieLogo) {
      const cookieResolved = resolveInstituteLogoSrc(cookieLogo)
      if (cookieResolved && cookieResolved !== NO_IMAGE_SRC) return toHttps(cookieResolved)
    }
    if (fetchedInstituteLogo) {
      const fetchedResolved = resolveInstituteLogoSrc(fetchedInstituteLogo)
      if (fetchedResolved && fetchedResolved !== NO_IMAGE_SRC) return toHttps(fetchedResolved)
    }
    return NO_IMAGE_SRC
  }, [instituteLogoFromContext, fetchedInstituteLogo])

  const headerLogoSrc =
    instituteDisplayLogo && instituteDisplayLogo !== NO_IMAGE_SRC
      ? instituteDisplayLogo
      : `${process.env.PUBLIC_URL || ''}/logo192.png`
  // Prefer data-URL for print/PDF (CORS-safe); keep remote URL for on-screen header
  const instituteLogoSrc = pdfInstituteLogo || headerLogoSrc
  const sessionName = Cookies.get('session_name') || ''

  useEffect(() => {
    setLogoError(false)
  }, [headerLogoSrc])

  useEffect(() => {
    if (instituteDisplayLogo && instituteDisplayLogo !== NO_IMAGE_SRC) return
    if (!institute_id) return

    let cancelled = false
    ;(async () => {
      try {
        const res = await ApiEndpoints.getAllInstitutesAssinged()
        const list = Array.isArray(res?.data?.data) ? res.data.data : []
        const matched = list.find(
          (row) => String(row?.institute_id ?? row?.id ?? '') === String(institute_id),
        )
        const logoPath = getInstituteLogoPathFromRecord(matched)
        if (!cancelled && logoPath) {
          const resolved = resolveInstituteLogoSrc(logoPath)
          setFetchedInstituteLogo(resolved)
          if (resolved && resolved !== NO_IMAGE_SRC) {
            Cookies.set('institute_logo', resolved, { expires: 7 })
          }
        }
      } catch {
        // keep placeholder branding
      }
    })()

    return () => {
      cancelled = true
    }
  }, [institute_id, instituteDisplayLogo])

  useEffect(() => {
    let isMounted = true
    const prepareLogoForPdf = async () => {
      if (!instituteDisplayLogo || instituteDisplayLogo === NO_IMAGE_SRC) {
        if (isMounted) setPdfInstituteLogo('')
        return
      }
      const resolved = await fetchImageAsDataUrlForPdf(instituteDisplayLogo)
      if (isMounted && resolved) setPdfInstituteLogo(resolved)
    }
    prepareLogoForPdf()
    return () => {
      isMounted = false
    }
  }, [instituteDisplayLogo])

  const filterCourseId = normalizeId(selectedCourse)
  const filterClassId = normalizeId(selectedClass)

  const courseName =
    courses.find((c) => String(c.id) === String(selectedCourse))?.name ||
    (filterCourseId ? `Course ${filterCourseId}` : '')
  const className =
    classes.find((c) => String(c.id) === String(selectedClass))?.class_name ||
    (filterClassId ? `Class ${filterClassId}` : '')

  const filteredSubjects = useMemo(
    () => getFilteredSubjects(subjects, filterCourseId, filterClassId),
    [subjects, filterCourseId, filterClassId],
  )
  const subjectRows = useMemo(() => getSubjectRows(filteredSubjects), [filteredSubjects])

  const examOptions = useMemo(() => {
    if (!Array.isArray(exams) || !filterCourseId) return []
    const norm = (v) => String(v ?? '').trim().toLowerCase()
    return exams.filter((ex) => {
      const c = norm(ex?.course_id ?? ex?.courseId ?? ex?.course?.id)
      if (c !== norm(filterCourseId)) return false
      const exClass = norm(
        ex?.class_id ?? ex?.classId ?? ex?.std_id ?? ex?.standard_id ?? ex?.class?.id,
      )
      if (!filterClassId) return true
      if (!exClass) return true
      return exClass === norm(filterClassId)
    })
  }, [exams, filterCourseId, filterClassId])

  const selectedExamOption = useMemo(
    () => examOptions.find((ex) => String(ex.id) === String(selectedExam)) || null,
    [examOptions, selectedExam],
  )

  const examName = selectedExamOption?.exam_name || ''

  const resolvedExamLocation = useMemo(() => {
    if (examLocation) return examLocation
    return selectedExamOption?.exam_location || ''
  }, [examLocation, selectedExamOption])

  const examRows = useMemo(() => {
    if (selectedExam && !loadingSchedules && subjectSchedules.length > 0) {
      return mapSubjectSchedulesToAdmitRows(subjectSchedules)
    }
    return buildExamRows(subjectRows, exams, filterCourseId, filterClassId)
  }, [
    selectedExam,
    loadingSchedules,
    subjectSchedules,
    subjectRows,
    exams,
    filterCourseId,
    filterClassId,
  ])

  const filteredStudents = useMemo(() => {
    const list = Array.isArray(students) ? students : []
    const query = studentSearch.trim().toLowerCase()
    if (!query) return list
    const tokens = query.split(/\s+/).filter(Boolean)
    return list.filter((student) => {
      const haystack = getStudentSearchText(student)
      return tokens.every((token) => haystack.includes(token))
    })
  }, [students, studentSearch])

  const previewStudentId = selectedStudentIds[0] || ''
  const previewStudent = profiles[previewStudentId] || null
  // Hidden on-screen preview: keep original preview name for restore
  // const previewStudentName =
  //   previewStudent?.name ||
  //   students.find((st) => getStudentId(st) === previewStudentId)?.name ||
  //   ''

  useEffect(() => {
    if (!selectedCourse || !selectedClass || !authToken) {
      setSubjects([])
      return
    }
    let cancelled = false
    ApiEndpoints.getAllSubjects()
      .then((res) => {
        if (!cancelled) {
          setSubjects(Array.isArray(res?.data?.data) ? res.data.data : [])
        }
      })
      .catch(() => {
        if (!cancelled) setSubjects([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedCourse, selectedClass, authToken])

  useEffect(() => {
    if (!previewStudentId || !selectedCourse || !selectedClass || !authToken) {
      setErrorCard('')
      return
    }

    if (profiles[previewStudentId]) {
      setLoadingCard(false)
      setErrorCard('')
      return
    }

    let cancelled = false
    const load = async () => {
      setLoadingCard(true)
      setErrorCard('')
      try {
        const loaded = await loadProfilesByIds([previewStudentId], profiles)
        if (cancelled) return
        if (loaded[previewStudentId]) {
          setProfiles((prev) => ({ ...prev, ...loaded }))
        } else {
          setErrorCard('Failed to load admit card.')
        }
      } catch (err) {
        if (!cancelled) {
          setErrorCard(err?.response?.data?.message || 'Failed to load admit card.')
        }
      } finally {
        if (!cancelled) setLoadingCard(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [previewStudentId, selectedCourse, selectedClass, authToken, profiles])

  useEffect(() => {
    if (!selectedStudentIds.length || !authToken) return
    const extras = selectedStudentIds.slice(1).filter((id) => !profiles[id])
    if (!extras.length) return

    let cancelled = false
    loadProfilesByIds(extras, profiles).then((loaded) => {
      if (!cancelled && Object.keys(loaded).length) {
        setProfiles((prev) => ({ ...prev, ...loaded }))
      }
    })
    return () => {
      cancelled = true
    }
  }, [selectedStudentIds, authToken, profiles])

  useEffect(() => {
    if (!selectedExam || !selectedCourse || !authToken) {
      setSubjectSchedules([])
      setExamLocation('')
      return
    }
    let cancelled = false
    const loadSchedules = async () => {
      setLoadingSchedules(true)
      try {
        const schedRes = await axios.get(`${BASE_URL}/exam/subject-schedules`, {
          headers: examHeaders,
          params: { exam_id: Number(selectedExam) },
        })
        if (cancelled) return
        const raw = schedRes.data?.data ?? schedRes.data ?? []
        setSubjectSchedules(Array.isArray(raw) ? raw : [])
        setExamLocation(schedRes.data?.exam_location || '')
      } catch {
        if (!cancelled) {
          setSubjectSchedules([])
          setExamLocation('')
        }
      } finally {
        if (!cancelled) setLoadingSchedules(false)
      }
    }
    loadSchedules()
    return () => {
      cancelled = true
    }
  }, [selectedExam, selectedCourse, authToken, examHeaders])

  // Sort By: load students from exam admit-cards API (Default=name, Roll Number=roll_number)
  // Also seed profiles from the same response so print/PDF does not need N× viewProfile calls.
  useEffect(() => {
    if (!selectedExam || !selectedCourse || !selectedClass || !authToken) {
      return
    }
    let cancelled = false
    setLoadingStudents(true)
    const sortBy = studentSortBy === 'roll_number' ? 'roll_number' : studentSortBy || 'name'
    ApiEndpoints.getExamAdmitCards({
      course_id: selectedCourse,
      class_id: selectedClass,
      exam_id: selectedExam,
      sort_by: sortBy,
      sort_order: 'asc',
    })
      .then((res) => {
        if (cancelled) return
        const ok = res?.data?.status
        const list = Array.isArray(res?.data?.students) ? res.data.students : []
        setStudents(ok ? list : [])
        if (ok && list.length) {
          const seeded = {}
          list.forEach((student) => {
            const id = getStudentId(student)
            if (id) seeded[id] = student
          })
          setProfiles((prev) => ({ ...prev, ...seeded }))
          setLoadingCard(false)
          setErrorCard('')
        }
      })
      .catch(() => {
        if (!cancelled) setStudents([])
      })
      .finally(() => {
        if (!cancelled) setLoadingStudents(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedExam, selectedCourse, selectedClass, studentSortBy, authToken])

  const handleCourseChange = (courseId) => {
    setSelectedCourse(courseId || '')
    setSelectedClass('')
    setSelectedExam('')
    setSelectedStudentIds([])
    setStudentSearch('')
    setClasses([])
    setStudents([])
    setProfiles({})
    if (courseId) {
      ApiEndpoints.getAllClassesByCourseId(courseId).then((res) => {
        setClasses(res.data.data || [])
      })
    }
  }

  const handleClassChange = (classId) => {
    setSelectedClass(classId || '')
    setSelectedExam('')
    setSelectedStudentIds([])
    setStudentSearch('')
    setStudents([])
    setProfiles({})
    if (classId) {
      const headers = {
        'institute-Id': institute_id || Cookies.get('institute_id'),
        Authorization: `Bearer ${token}`,
      }
      setLoadingStudents(true)
      axios
        .get(BASE_URL + `/get/student/by-class?class_id=${classId}`, { headers })
        .then((res) => setStudents(res.data.data || []))
        .catch(() => setStudents([]))
        .finally(() => setLoadingStudents(false))
    }
  }

  const handleStudentToggle = (studentId) => {
    const id = String(studentId)
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    )
  }

  const allFilteredSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((student) => selectedStudentIds.includes(getStudentId(student)))

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredStudents.map(getStudentId).filter(Boolean)
    if (allFilteredSelected) {
      setSelectedStudentIds((prev) => prev.filter((id) => !filteredIds.includes(id)))
      return
    }
    setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...filteredIds])))
  }

  const handleRefresh = useCallback(() => {
    setSelectedCourse('')
    setSelectedClass('')
    setSelectedExam('')
    setSelectedStudentIds([])
    setStudentSearch('')
    setStudentSortBy('')
    setClasses([])
    setStudents([])
    setProfiles({})
    setSubjects([])
    setExams([])
    setSubjectSchedules([])
    setErrorCard('')
    setPreparingAction('')
    setExportingAll(false)
  }, [])

  const ensureSelectedProfiles = async () => {
    const loaded = await loadProfilesByIds(selectedStudentIds, profiles)
    if (Object.keys(loaded).length) {
      setProfiles((prev) => ({ ...prev, ...loaded }))
    }
    return { ...profiles, ...loaded }
  }

  const handlePrint = async () => {
    if (!selectedStudentIds.length) return
    setPreparingAction('print')
    await ensureSelectedProfiles()
    window.setTimeout(() => {
      window.print()
      setPreparingAction('')
    }, 500)
  }

  const handleDownloadPdf = async () => {
    if (!selectedStudentIds.length) return
    setPreparingAction('pdf')
    setExportingAll(true)

    let exportStage = null
    try {
      const latestProfiles = await ensureSelectedProfiles()
      const expectedCount = selectedStudentIds.filter((id) => latestProfiles[id]).length
      if (!expectedCount) {
        setErrorCard('Failed to load selected student profiles for download.')
        return
      }

      await waitForPaint()
      const cardNodes = await waitForAdmitCardNodes(expectedCount)
      if (!cardNodes.length) {
        setErrorCard('Admit cards were not ready for download. Please try again.')
        return
      }

      exportStage = document.createElement('div')
      exportStage.setAttribute('data-bulk-admit-export-stage', 'true')
      exportStage.style.cssText = [
        'position:fixed',
        'left:0',
        'top:0',
        'width:794px',
        'max-width:794px',
        'padding:16px',
        'box-sizing:border-box',
        'background:#ffffff',
        'z-index:-1',
        'pointer-events:none',
      ].join(';')
      document.body.appendChild(exportStage)

      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 8
      const maxWidth = pageWidth - margin * 2
      const maxHeight = pageHeight - margin * 2

      for (let i = 0; i < cardNodes.length; i += 1) {
        exportStage.replaceChildren(cardNodes[i].cloneNode(true))
        const clonedCard = exportStage.querySelector('.admit-card-root') || exportStage.firstElementChild
        if (clonedCard) {
          clonedCard.style.display = 'block'
          clonedCard.style.visibility = 'visible'
          clonedCard.style.position = 'relative'
        }
        await inlineImagesForPdf(exportStage)
        await waitForImages(exportStage)
        await waitForPaint()

        const canvas = await html2canvas(exportStage, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
        })

        const imgWidth = maxWidth
        let imgHeight = (canvas.height * imgWidth) / canvas.width
        let drawWidth = imgWidth
        let drawHeight = imgHeight
        if (imgHeight > maxHeight) {
          drawHeight = maxHeight
          drawWidth = (canvas.width * drawHeight) / canvas.height
        }

        if (i > 0) pdf.addPage()
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 0.95),
          'JPEG',
          margin,
          margin,
          drawWidth,
          drawHeight,
          undefined,
          'FAST',
        )
      }

      pdf.save(`Bulk-Admit-Card-${examName || 'exam'}.pdf`)
    } catch (error) {
      console.error('Bulk admit card PDF failed:', error)
      setErrorCard('Failed to download admit cards. Please try again.')
    } finally {
      if (exportStage?.parentNode) {
        exportStage.parentNode.removeChild(exportStage)
      }
      setExportingAll(false)
      setPreparingAction('')
    }
  }

  const showCard = Boolean(
    previewStudentId && selectedCourse && selectedClass && previewStudent,
  )
  const canPrint =
    showCard && selectedStudentIds.length > 0 && !(selectedExam && loadingSchedules)

  const studentsReadyForPrint = (() => {
    const selectedSet = new Set(selectedStudentIds.map(String))
    const orderedIds = filteredStudents
      .map(getStudentId)
      .filter((id) => id && selectedSet.has(id))
    const remaining = selectedStudentIds.filter((id) => !orderedIds.includes(String(id)))
    return [...orderedIds, ...remaining]
      .map((id) => {
        const fromProfile = profiles[id]
        if (fromProfile) return fromProfile
        return filteredStudents.find((st) => getStudentId(st) === String(id)) || null
      })
      .filter(Boolean)
  })()

  return (
    <>
      <style>
        {`
          .filter-card-container { overflow: visible !important; position: relative !important; z-index: 30 !important; }
          .filter-card-container .card { overflow: visible !important; position: relative !important; z-index: 30 !important; }
          .filter-card-container .card-body { overflow: visible !important; position: relative !important; z-index: 30 !important; }
          .filter-card-container .row { overflow: visible !important; position: relative !important; }
          .filter-card-container .row > [class*='col-'] {
            overflow: visible !important;
            position: relative !important;
          }
          .filter-card-container .rbt { position: relative !important; z-index: 1 !important; }
          .filter-card-container .rbt:focus-within,
          .filter-card-container .rbt.open {
            z-index: 50 !important;
          }
          .rbt-menu {
            z-index: 9999 !important;
            position: absolute !important;
            max-height: 260px !important;
            overflow-y: auto !important;
            background: #ffffff !important;
            box-shadow: 0 8px 24px rgba(17, 24, 39, 0.16) !important;
          }
          .rbt-menu.dropdown-menu {
            z-index: 9999 !important;
            position: absolute !important;
            background: #ffffff !important;
          }
          .bulk-student-section { position: relative; z-index: 1; }
          @media (max-width: 767.98px) {
            .filter-card-container .row > [class*='col-']:nth-child(1) { z-index: 5; }
            .filter-card-container .row > [class*='col-']:nth-child(2) { z-index: 4; }
            .filter-card-container .row > [class*='col-']:nth-child(3) { z-index: 3; }
            .filter-card-container .row > [class*='col-']:focus-within { z-index: 20; }
            .filter-card-container .rbt:focus-within,
            .filter-card-container .rbt.open {
              z-index: 30 !important;
            }
            .rbt-menu {
              max-height: 220px !important;
            }
          }
          .admit-top-header {
            margin-top: -12px;
            border-bottom: 1px solid #d1d3d4;
            background: #f3f4f6;
            padding: 12px 28px;
            width: 100%;
          }
          .admit-top-header-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }
          .admit-title-wrap {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .admit-header-logo {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            overflow: hidden;
            background: #223843;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .admit-page { max-width: 920px; margin: 24px auto; padding: 0 16px; }
          .bulk-student-panel {
            background: transparent;
          }
          .bulk-student-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 0 0 12px;
            background: transparent;
          }
          .bulk-student-search {
            flex: 1 1 240px;
            position: relative;
            min-width: 180px;
          }
          .bulk-student-search input {
            width: 100%;
            height: 40px;
            border: 1px solid #D1D3D4;
            border-radius: 8px;
            padding: 0 12px 0 36px;
            font-size: 14px;
            position: relative;
            z-index: 1;
            background: #fff;
          }
          .bulk-student-search svg {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #6b7280;
            pointer-events: none;
            z-index: 1;
          }
          .bulk-student-sort-select {
            width: 100%;
            height: 40px;
            border: 1px solid #D1D3D4;
            border-radius: 8px;
            padding: 0 12px;
            font-size: 14px;
            background: #fff;
            color: #111827;
          }
          .bulk-student-list {
            max-height: 420px;
            overflow: auto;
          }
          .bulk-select-all-check {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
            white-space: nowrap;
            user-select: none;
            cursor: pointer !important;
          }
          .bulk-select-all-check.is-disabled {
            cursor: not-allowed !important;
            opacity: 0.2;
          }
          .bulk-select-all-check input[type='checkbox'] {
            width: 1em;
            height: 1em;
            margin: 0;
            flex-shrink: 0;
            cursor: pointer !important;
          }
          .bulk-select-all-check.is-disabled input[type='checkbox'] {
            cursor: not-allowed !important;
          }
          .bulk-select-all-check span {
            cursor: inherit !important;
          }
          .bulk-action-btn {
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          }
          .bulk-action-btn:hover:not(:disabled) {
            background-color: #223843 !important;
            color: #ffffff !important;
            border-color: #223843 !important;
            box-shadow: 0 2px 8px rgba(34, 56, 67, 0.35);
          }
          .bulk-student-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          .bulk-student-table thead th {
            background: #f3f4f6;
            color: #374151;
            font-size: 13px;
            font-weight: 600;
            padding: 12px 14px;
            border-bottom: 1px solid #D1D3D4;
            white-space: nowrap;
            text-align: left;
          }
          .bulk-student-table tbody td {
            font-size: 14px;
            padding: 12px 14px;
            border-bottom: 1px solid #F3F4F6;
            color: #111827;
            vertical-align: middle;
          }
          .bulk-student-table tbody tr {
            cursor: pointer;
            background: transparent;
          }
          .bulk-student-table tbody tr:hover { background: #F3F4F6; }
          .bulk-student-table tbody tr.selected { background: #EEF4F6; }
          .bulk-student-table tbody tr:last-child td { border-bottom: none; }
          .bulk-student-table .form-check {
            margin: 0;
            min-height: auto;
            display: flex;
            align-items: center;
            padding-left: 1.5em;
          }
          .bulk-student-table .form-check-input {
            cursor: pointer;
            margin-top: 0;
          }
          .bulk-student-name-cell {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;
          }
          .bulk-student-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            overflow: hidden;
            background: #223843;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            flex-shrink: 0;
            position: relative;
          }
          .bulk-student-avatar img {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .bulk-student-name {
            font-size: 14px;
            font-weight: 600;
            color: #111827;
            line-height: 1.3;
          }
          .bulk-student-empty {
            padding: 28px 0;
            text-align: center;
            color: #6b7280;
            font-size: 14px;
          }
          .bulk-admit-extra { display: none; }
          /* Hide admit card UI on screen; still cloned for PDF and shown for print */
          .exam-sheet-bulk-admit-root .admit-page { display: none; }
          .bulk-admit-card-page { page-break-after: always; break-after: page; }
          .bulk-admit-card-page:last-child { page-break-after: auto; break-after: auto; }
          [data-bulk-admit-export-stage] .admit-card-root {
            display: block !important;
            visibility: visible !important;
            position: relative !important;
            box-shadow: none !important;
            max-width: 100% !important;
            overflow: visible !important;
          }
          @media (max-width: 768px) {
            .admit-top-header { margin-top: 55px; padding: 12px 16px; }
          }

          @media print {
            @page { size: A4; margin: 12mm 18mm 12mm 12mm; }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              overflow: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body:has(.exam-sheet-bulk-admit-root) aside,
            body:has(.exam-sheet-bulk-admit-root) nav,
            body:has(.exam-sheet-bulk-admit-root) .navbar,
            body:has(.exam-sheet-bulk-admit-root) .sidebar,
            body:has(.exam-sheet-bulk-admit-root) [class*="sidebar"],
            body:has(.exam-sheet-bulk-admit-root) [class*="Sidenavbar"],
            body:has(.exam-sheet-bulk-admit-root) [class*="sidenavbar"],
            body:has(.exam-sheet-bulk-admit-root) .offcanvas,
            body:has(.exam-sheet-bulk-admit-root) .offcanvas-backdrop,
            body:has(.exam-sheet-bulk-admit-root) .no-print {
              display: none !important;
              visibility: hidden !important;
              width: 0 !important;
              max-width: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .App {
              display: block !important;
              overflow: visible !important;
              width: 100% !important;
              max-width: 100% !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .app-main-content {
              display: block !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              flex: none !important;
              overflow: visible !important;
              min-width: 0 !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root {
              margin-left: 0 !important;
              margin-right: 0 !important;
              margin-top: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              overflow: visible !important;
              box-sizing: border-box !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .container-fluid,
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .container-fluid > div {
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              max-width: 100% !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .admit-top-header {
              display: none !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .admit-page {
              display: block !important;
              margin: 0 !important;
              padding: 0 6mm !important;
              max-width: 100% !important;
              width: 100% !important;
              box-sizing: border-box !important;
              overflow: visible !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .admit-card-root {
              max-width: 100% !important;
              width: 100% !important;
              box-sizing: border-box !important;
              box-shadow: none !important;
              overflow: visible !important;
              page-break-inside: auto !important;
              break-inside: auto !important;
              padding: 10px 6mm !important;
            }
            /* Keep exam-table borders inside the printable area (Sr. No. left + Time right) */
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table {
              width: 100% !important;
              max-width: 100% !important;
              table-layout: fixed !important;
              border-collapse: separate !important;
              border-spacing: 0 !important;
              box-sizing: border-box !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th,
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td {
              border: 1px solid #111827 !important;
              box-sizing: border-box !important;
              overflow-wrap: anywhere !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th + th,
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td + td {
              border-left: none !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table tbody tr td {
              border-top: none !important;
            }
            /* Sr. No. | Subject Name | Paper Type | Exam Date | Time */
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th:nth-child(1),
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td:nth-child(1) {
              width: 8% !important;
              text-align: center !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th:nth-child(2),
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td:nth-child(2) {
              width: 26% !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th:nth-child(3),
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td:nth-child(3) {
              width: 24% !important;
              white-space: normal !important;
              word-break: break-word !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th:nth-child(4),
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td:nth-child(4) {
              width: 18% !important;
              white-space: nowrap !important;
              overflow-wrap: normal !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table th:nth-child(5),
            body:has(.exam-sheet-bulk-admit-root) .exam-sheet-bulk-admit-root .exam-table td:nth-child(5) {
              width: 24% !important;
              white-space: normal !important;
              overflow-wrap: anywhere !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .bulk-admit-extra {
              display: block !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .bulk-admit-card-page {
              padding-top: 10mm !important;
              padding-bottom: 0 !important;
              box-sizing: border-box !important;
              width: 100% !important;
              max-width: 100% !important;
              overflow: visible !important;
              page-break-inside: auto !important;
              break-inside: auto !important;
              page-break-after: auto !important;
              break-after: auto !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .bulk-admit-card-page:first-child {
              padding-top: 0 !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .bulk-admit-card-page + .bulk-admit-card-page {
              page-break-before: always !important;
              break-before: page !important;
            }
            /* One watermark per page — fixed positioning stacks when many cards are selected */
            body:has(.exam-sheet-bulk-admit-root) .admit-watermark-logo-wrapper {
              position: absolute !important;
              top: 55% !important;
              left: 50% !important;
              transform: translate(-50%, -50%) !important;
              width: 72% !important;
              max-width: 420px !important;
              height: auto !important;
              opacity: 0.2 !important;
              z-index: 0 !important;
            }
            body:has(.exam-sheet-bulk-admit-root) .admit-watermark-logo {
              width: 100% !important;
              height: auto !important;
              object-fit: contain !important;
            }
          }
        `}
      </style>

      <div
        className={`admit-main-wrap exam-sheet-bulk-admit-root${exportingAll ? ' bulk-exporting' : ''}`}
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div className="container-fluid p-0">
          <div style={{ marginLeft: isMobile ? '0' : '-40px' }}>
            <div className="admit-top-header">
              <div className="admit-top-header-row">
                <div className="admit-title-wrap">
                  <div className="admit-header-logo">
                    {!logoError ? (
                      <img
                        src={headerLogoSrc}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <h3
                    className="m-0 fw-bold"
                    style={{
                      letterSpacing: '-0.02em',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#223843',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Print Bulk Admit Card
                    {selectedStudentIds.length
                      ? ` — ${selectedStudentIds.length} selected`
                      : ''}
                  </h3>
                </div>
                {!isMobile && <ProfileDropdown logout={logout} />}
              </div>
            </div>
          </div>
        </div>

        <div className="no-print">
          <Container fluid className="mt-4 px-3 px-md-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <button
                type="button"
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                title={showFilterCard ? 'Hide filters' : 'Show filters'}
              >
                <BsFunnelFill size={20} />
              </button>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  className="bulk-action-btn"
                  variant="outline-secondary"
                  onClick={() => navigate('/ExamSheet')}
                >
                  <FaArrowLeft className="me-1" />
                  Exam sheet
                </Button>
                {canPrint && (
                  <>
                    <Button
                      className="bulk-action-btn"
                      variant="outline-secondary"
                      onClick={handlePrint}
                      disabled={Boolean(preparingAction)}
                    >
                      {preparingAction === 'print' ? (
                        <Spinner animation="border" size="sm" className="me-1" />
                      ) : (
                        <FaPrint className="me-1" />
                      )}
                      Print Admit Cards
                      {/* Print {selectedStudentIds.length} card */}
                      {/* {selectedStudentIds.length !== 1 ? 's' : ''} */}
                    </Button>
                    <Button
                      onClick={handleDownloadPdf}
                      disabled={Boolean(preparingAction)}
                      style={{
                        backgroundColor: '#223843',
                        color: '#ffffff',
                        borderColor: '#223843',
                        fontWeight: 600,
                      }}
                    >
                      {preparingAction === 'pdf' ? (
                        <Spinner animation="border" size="sm" className="me-1" />
                      ) : (
                        <FaDownload className="me-1" />
                      )}
                      Download Admit Cards
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div
              className="filter-card-container"
              style={{
                maxHeight: showFilterCard ? '2000px' : '0',
                overflow: showFilterCard ? 'visible' : 'hidden',
                transition: 'max-height 0.3s ease-in-out',
                marginBottom: showFilterCard ? '24px' : '0',
              }}
            >
              {showFilterCard && (
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    overflow: 'visible',
                  }}
                >
                  <div
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BsSearch style={{ color: '#fff', fontSize: '18px' }} />
                        </div>
                        <div className="text-start">
                          <h5 className="m-0 fw-semibold" style={{ color: '#111827', fontSize: '16px' }}>
                            Filter students
                          </h5>
                          <p className="m-0 mt-1 text-muted small">
                            Choose course, class, and exam name. Students appear below for selection.
                            {/* Choose course, class, exam name, and one or more students. The preview
                            shows one admit card; print includes every selected student. */}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        title="Reset"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                  <Card.Body className="p-4" style={{ overflow: 'visible' }}>
                    <Row className="g-4 text-start">
                      <Col xs={12} md={4}>
                        <label className="fw-semibold mb-2 d-block small text-muted">Course</label>
                        <Typeahead
                          id="bulk-admit-course"
                          labelKey="name"
                          filterBy={['name']}
                          onChange={(selected) => {
                            const c = selected[0]
                            handleCourseChange(c ? c.id : '')
                          }}
                          options={courses}
                          placeholder="Select course"
                          selected={courses.filter((cs) => String(cs.id) === String(selectedCourse))}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <label className="fw-semibold mb-2 d-block small text-muted">Class</label>
                        <Typeahead
                          id="bulk-admit-class"
                          labelKey="class_name"
                          filterBy={['class_name']}
                          onChange={(selected) => {
                            const c = selected[0]
                            handleClassChange(c ? c.id : '')
                          }}
                          options={classes}
                          placeholder="Select class"
                          selected={classes.filter((cs) => String(cs.id) === String(selectedClass))}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <label className="fw-semibold mb-2 d-block small text-muted">Exam Name</label>
                        <Typeahead
                          id="bulk-admit-exam"
                          labelKey={(opt) => opt.exam_name || 'Exam'}
                          filterBy={(option, props) => {
                            const q = String(props.text || '').trim().toLowerCase()
                            if (!q) return true
                            const name = String(option?.exam_name || '').toLowerCase()
                            const date = String(option?.start_date || '').toLowerCase()
                            return name.includes(q) || date.includes(q)
                          }}
                          onChange={(selected) => {
                            const ex = selected[0]
                            setSelectedExam(ex ? String(ex.id) : '')
                          }}
                          options={examOptions}
                          placeholder={
                            !selectedCourse
                              ? 'Select a course first'
                              : examOptions.length === 0
                                ? 'No exams for this course/class'
                                : 'Select exam name'
                          }
                          disabled={!selectedCourse}
                          selected={examOptions.filter((e) => String(e.id) === String(selectedExam))}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                            },
                          }}
                        />
                        {selectedExam && loadingSchedules && (
                          <div className="small text-muted mt-2">Loading subject schedule…</div>
                        )}
                      </Col>
                    </Row>

                    {selectedExam && (
                      <Row className="g-4 text-start mt-1">
                        <Col xs={12} md={8}>
                          <label className="fw-semibold mb-2 d-block small text-muted">
                            Search Student
                          </label>
                          <div className="bulk-student-search">
                            <BsSearch size={14} />
                            <input
                              type="text"
                              value={studentSearch}
                              onChange={(e) => setStudentSearch(e.target.value)}
                              onMouseDown={(e) => e.stopPropagation()}
                              autoComplete="off"
                              placeholder="Search by name, admission no. or roll no."
                            />
                          </div>
                        </Col>
                        <Col xs={12} md={4}>
                          <label
                            className="fw-semibold mb-2 d-block small text-muted"
                            htmlFor="bulk-admit-sort-by"
                          >
                            Sort By
                          </label>
                          <select
                            id="bulk-admit-sort-by"
                            className="bulk-student-sort-select"
                            value={studentSortBy}
                            onChange={(e) => setStudentSortBy(e.target.value)}
                          >
                            <option value="">Default</option>
                            <option value="roll_number">Roll Number</option>
                          </select>
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              )}
            </div>

            {selectedExam && (
              <div className="text-start mb-4 bulk-student-section">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="fw-semibold mb-0 small text-muted">Student</label>
                  <div className="d-flex align-items-center gap-3">
                    <span className="small text-muted">
                      {selectedStudentIds.length} of {students.length} selected
                    </span>
                    <label
                      className={`bulk-select-all-check${!filteredStudents.length ? ' is-disabled' : ''}`}
                      htmlFor="bulk-admit-select-all"
                      style={{
                        cursor: filteredStudents.length ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <input
                        type="checkbox"
                        id="bulk-admit-select-all"
                        disabled={!filteredStudents.length}
                        checked={allFilteredSelected}
                        onChange={handleSelectAllFiltered}
                        style={{
                          cursor: filteredStudents.length ? 'pointer' : 'not-allowed',
                        }}
                      />
                      <span>
                        {allFilteredSelected ? 'Clear filtered' : 'Select all'}
                      </span>
                    </label>
                  </div>
                </div>
                <div className="bulk-student-panel">
                  <div className="bulk-student-list">
                    {loadingStudents ? (
                      <div className="bulk-student-empty">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading students…
                      </div>
                    ) : filteredStudents.length === 0 ? (
                      <div className="bulk-student-empty">
                        {studentSearch
                          ? 'No students match your search.'
                          : 'No students found in this class.'}
                      </div>
                    ) : (
                      <table className="bulk-student-table">
                        <thead>
                          <tr>
                            <th style={{ width: '48px' }} />
                            <th>Student</th>
                            <th>Admission No.</th>
                            <th>Roll No.</th>
                            <th>Father Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStudents.map((student) => {
                            const id = getStudentId(student)
                            const selected = selectedStudentIds.includes(id)
                            const photo = student?.student_image || student?.image || ''
                            return (
                              <tr
                                key={id || student.name}
                                className={selected ? 'selected' : ''}
                                onClick={(e) => {
                                  if (e.target.closest('.form-check')) return
                                  handleStudentToggle(id)
                                }}
                              >
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    id={`bulk-student-${id}`}
                                    checked={selected}
                                    onChange={() => handleStudentToggle(id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                                <td>
                                  <div className="bulk-student-name-cell">
                                    <div className="bulk-student-avatar">
                                      {getInitials(student?.name)}
                                      {photo ? (
                                        <img
                                          src={photo}
                                          alt=""
                                          onError={(e) => {
                                            e.currentTarget.style.display = 'none'
                                          }}
                                        />
                                      ) : null}
                                    </div>
                                    <span className="bulk-student-name">
                                      {student?.name || 'Unnamed student'}
                                    </span>
                                  </div>
                                </td>
                                <td>{student?.admission_number || 'N/A'}</td>
                                <td>{student?.roll_number || '—'}</td>
                                <td>{student?.father_name || '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Hidden on-screen admit card preview UI — do not remove
            {loadingCard && (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            */}

            {errorCard && !loadingCard && (
              <div
                className="mb-4 p-3 rounded"
                style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b' }}
              >
                {errorCard}
              </div>
            )}

            {/* Hidden on-screen admit card preview UI — do not remove
            {showCard && !loadingCard && (
              <p className="text-muted small mb-0">
                Previewing {previewStudentName || 'selected student'}
                {selectedStudentIds.length > 1
                  ? `. Print / PDF will include all ${selectedStudentIds.length} selected students.`
                  : '.'}
              </p>
            )}
            */}
          </Container>
        </div>

        {/* Hidden on-screen admit card UI — markup kept for print/PDF. Original visible preview:
        <div className="admit-page">
          {showCard && !loadingCard && selectedExam && loadingSchedules && (
            <div className="d-flex flex-column justify-content-center align-items-center gap-2 py-5 text-muted">
              <Spinner animation="border" variant="primary" />
              <span>Loading exam subject schedule…</span>
            </div>
          )}
          {showCard && !loadingCard && !(selectedExam && loadingSchedules) && (
            <div ref={printContainerRef}>
              {studentsReadyForPrint.map((student, index) => {
                const isPreview = String(student?.id) === String(previewStudentId)
                return (
                  <div
                    key={student?.id || index}
                    className={`bulk-admit-card-page${isPreview ? '' : ' bulk-admit-extra'}`}
                  >
                    <AdmitCardPrintView
                      student={student}
                      courseName={courseName}
                      className={className}
                      instituteName={instituteName}
                      examName={examName}
                      sessionName={sessionName}
                      instituteLogoSrc={instituteLogoSrc}
                      examRows={examRows}
                      examLocation={resolvedExamLocation}
                    />
                  </div>
                )
              })}
            </div>
          )}
          {!showCard && !loadingCard && selectedStudentIds.length > 0 && (
            <p className="text-muted text-center py-4">
              Select course and class to load the admit card.
            </p>
          )}
        </div>
        */}

        <div className="admit-page" aria-hidden="true">
          {showCard && !loadingCard && !(selectedExam && loadingSchedules) && (
            <div ref={printContainerRef}>
              {studentsReadyForPrint.map((student, index) => (
                <div
                  key={student?.id || index}
                  className="bulk-admit-card-page bulk-admit-extra"
                >
                  {useShriHitTemplate ? (
                    <AdmitCardShriHit1
                      student={student}
                      courseName={courseName}
                      className={className}
                      instituteName={instituteName}
                      examName={examName}
                      sessionName={sessionName}
                      instituteLogoSrc={instituteLogoSrc}
                      examRows={examRows}
                      examLocation={resolvedExamLocation}
                    />
                  ) : (
                    <AdmitCardPrintView
                      student={student}
                      courseName={courseName}
                      className={className}
                      instituteName={instituteName}
                      examName={examName}
                      sessionName={sessionName}
                      instituteLogoSrc={instituteLogoSrc}
                      examRows={examRows}
                      examLocation={resolvedExamLocation}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
